import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowUpRight, Shield, TrendingUp, Rocket } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  titan_care: [
    "Hosting",
    "SSL / security",
    "Backups",
    "Uptime monitoring",
    "Software updates",
    "Monthly site health checks",
    "30 minutes of edits",
  ],
  titan_performance: [
    "Everything in Titan Care",
    "Analytics review",
    "Speed optimization",
    "Conversion monitoring",
    "Google Business monitoring",
    "2 hours of changes / content",
    "Quarterly optimization",
  ],
  titan_care_growth: [
    "Everything in Care + Performance",
    "SEO strategy",
    "New landing pages & content",
    "Conversion optimization",
    "A / B testing",
    "Reputation strategy",
    "CRM / funnel optimization",
    "Monthly strategy review",
  ],
};

const META = {
  titan_care: { tag: "Peace of mind", icon: Shield },
  titan_performance: { tag: "Keep improving", icon: TrendingUp },
  titan_care_growth: { tag: "Fully managed growth", icon: Rocket },
};

const DISPLAY_NAME = {
  // UI shows "Titan Growth" per client ask, even though internal id/receipt label differs
  titan_care: "Titan Care",
  titan_performance: "Titan Performance",
  titan_care_growth: "Titan Growth",
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const CarePlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/payments/care-plans`)
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please enter your name and email.");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: selected.lookup_key,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const featured = "titan_performance";

  return (
    <section
      id="care"
      data-testid="care-section"
      className="relative py-24 md:py-32 bg-[#0A1428]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-8">
            <SectionLabel testid="care-label">Ongoing Care</SectionLabel>
            <Reveal>
              <h2 className="font-display font-bold tracking-tight text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                A great site is launched. <span className="text-[#1E6BFF]">A winning site is maintained.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4">
            <Reveal delay={0.1}>
              <p className="text-gray-400 leading-relaxed">
                Monthly care plans keep your site fast, secure and converting — month after month.
                Cancel anytime.
              </p>
            </Reveal>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-gray-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading plans…
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((plan, i) => {
              const meta = META[plan.lookup_key] || {};
              const Icon = meta.icon;
              const isFeatured = plan.lookup_key === featured;
              const displayName = DISPLAY_NAME[plan.lookup_key] || plan.name;
              return (
                <Reveal key={plan.lookup_key} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    data-testid={`care-card-${plan.lookup_key}`}
                    className={`relative h-full rounded-2xl p-8 flex flex-col ${
                      isFeatured
                        ? "border-2 border-[#1E6BFF] bg-gradient-to-b from-[#1E6BFF]/15 to-transparent glow-blue"
                        : "border border-white/10 bg-white/[0.03]"
                    }`}
                  >
                    {Icon && (
                      <div className="w-11 h-11 rounded-xl bg-[#1E6BFF]/15 border border-[#1E6BFF]/30 flex items-center justify-center mb-5">
                        <Icon size={20} className="text-[#1E6BFF]" />
                      </div>
                    )}
                    <h3 className="font-display font-semibold text-2xl text-white">{displayName}</h3>
                    {meta.tag && <p className="text-gray-400 text-sm mt-1 mb-5">{meta.tag}</p>}
                    <div className="mb-6 flex items-baseline gap-1">
                      <span className="font-display font-bold text-5xl text-white">
                        {money(plan.amount)}
                      </span>
                      <span className="text-gray-500 text-sm">/ month</span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {(FEATURES[plan.lookup_key] || []).map((f) => (
                        <li key={f} className="flex items-start gap-3 text-gray-200 text-sm">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isFeatured ? "bg-[#1E6BFF]" : "bg-[#1E6BFF]/15 border border-[#1E6BFF]/30"}`}>
                            <Check size={12} className={isFeatured ? "text-white" : "text-[#1E6BFF]"} />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => setSelected({ ...plan, displayName })}
                      data-testid={`subscribe-${plan.lookup_key}`}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3.5 transition-colors duration-200 ${
                        isFeatured
                          ? "bg-[#1E6BFF] hover:bg-[#3B82F6] text-white"
                          : "border border-white/20 hover:border-white/50 text-white"
                      }`}
                    >
                      Subscribe
                      <ArrowUpRight size={16} />
                    </button>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal delay={0.2}>
          <p className="text-center text-xs text-gray-500 mt-10">
            Billed monthly. Cancel anytime from your email receipt.
          </p>
        </Reveal>
      </div>

      {/* Subscribe modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && !submitting && setSelected(null)}>
        <DialogContent data-testid="subscribe-dialog" className="bg-[#0A1428]/95 backdrop-blur-2xl border border-[#1E6BFF]/40 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Subscribe to {selected?.displayName}
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-1">
              Enter your details and we'll take you to secure checkout.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={startCheckout} className="space-y-4 pt-2">
            <Input
              data-testid="subscribe-name"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
              required
            />
            <Input
              data-testid="subscribe-email"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-[#050A14]/60 border-white/15 text-white placeholder:text-gray-500 focus-visible:ring-[#1E6BFF] h-12"
              required
            />
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between">
              <span className="text-gray-300 text-sm">{selected?.displayName}</span>
              <span className="font-display font-bold text-white text-xl">
                {selected ? `${money(selected.amount)}/mo` : "$0"}
              </span>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="subscribe-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] disabled:opacity-60 text-white font-semibold px-8 py-3.5 transition-colors duration-200 glow-blue"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Redirecting…
                </>
              ) : (
                "Continue to secure checkout"
              )}
            </button>
            <p className="text-center text-xs text-gray-500">Powered by Stripe · Cancel anytime</p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CarePlans;
